import dayjs from 'dayjs';
import { create } from 'zustand';
import {
  CheckInCheckOutExportRequest,
  CheckinHistoryType,
  CheckInType,
  deleteCheckin,
  fetchCheckInCheckOut,
  fetchCheckInCheckOutExport,
  fetchCheckInCheckOutHistory,
  FetchCheckInCheckOutHistoryRequest,
  FetchCheckInCheckOutRequest,
  putCheckInCheckOut,
  PutCheckInCheckOutRequest,
} from './api/check-in-check-out-api';
import { useAlertStore } from './use-alert-store';
import { PaginationType } from '../types/pagination';

export const getOvertime = (endTime?: string | null, shiftEndTime?: string | null) => {
  const delta = dayjs(shiftEndTime).diff(dayjs(endTime), 'minute');
  const isOvertime = delta < 0;

  return isOvertime ? -delta : null;
};

export const getDelay = (startTime?: string | null, shiftStartTime?: string | null) => {
  const delta = dayjs(startTime).diff(dayjs(shiftStartTime), 'minute');
  const isDelay = delta < 0;

  return isDelay ? -delta : null;
};

interface Store {
  checkins?: CheckInType[];
  checkinsHistory?: CheckinHistoryType[];
  fetchCheckInCheckOut: (params: FetchCheckInCheckOutRequest) => Promise<void>;
  fetchCheckInCheckOutExport: (params: CheckInCheckOutExportRequest) => Promise<void>;
  putCheckInCheckOut: (params: PutCheckInCheckOutRequest) => Promise<void>;
  deleteCheckin: (id: string, reason: string) => Promise<void>;
  fetchCheckInCheckOutHistory: (params: FetchCheckInCheckOutHistoryRequest) => Promise<void>;
  isLoadingFetch?: boolean;
  isLoadingHistoryFetch?: boolean;
  isLoadingExportFetch?: boolean;
  params?: FetchCheckInCheckOutRequest;
  pagination?: PaginationType;
  paginationHistory?: PaginationType;
  handleUpdateCheckInCheckOut: (checkin: CheckInType) => void;
}

export const useCheckInCheckOutStore = create<Store>((set, get) => ({
  fetchCheckInCheckOut: async (params) => {
    set({ isLoadingFetch: true });
    try {
      const { checkins, ...other } = await fetchCheckInCheckOut(params);

      const isInfinite = !!params.infinite;
      const updatedList = isInfinite ? get().checkins?.concat(checkins) : checkins;

      set({ checkins: updatedList, params, pagination: other });
    } catch (e) {
      console.error(e);
    } finally {
      set({ isLoadingFetch: false });
    }
  },

  putCheckInCheckOut: async (params) => {
    try {
      set({ isLoadingFetch: true });
      await putCheckInCheckOut(params);

      const state = useCheckInCheckOutStore.getState();

      if (state.params) {
        await state.fetchCheckInCheckOut(state.params);
      } else {
        await state.fetchCheckInCheckOut({ with_archive: false });
      }

      useAlertStore.getState().add({
        variant: 'success',
        label: 'Изменения сохранены',
      });
    } catch (e) {
      console.error(e);
      useAlertStore.getState().add({
        variant: 'danger',
        label: 'Не удалось сохранить изменения',
      });

      throw new Error();
    } finally {
      set({ isLoadingFetch: false });
    }
  },

  fetchCheckInCheckOutExport: async (params) => {
    set({ isLoadingExportFetch: true });
    try {
      await fetchCheckInCheckOutExport(params);
    } catch (e) {
      console.error(e);
    } finally {
      set({ isLoadingExportFetch: false });
    }
  },

  deleteCheckin: async (id, reason) => {
    try {
      set({ isLoadingFetch: true });
      await deleteCheckin(id, reason);

      const state = useCheckInCheckOutStore.getState();

      if (state.params) {
        await state.fetchCheckInCheckOut(state.params);
      } else {
        await state.fetchCheckInCheckOut({ with_archive: false });
      }

      useAlertStore.getState().add({
        variant: 'success',
        label: 'Успешно удалено',
      });
    } catch (e) {
      console.error(e);
      useAlertStore.getState().add({
        variant: 'danger',
        label: 'Не удалось удалить',
      });
    } finally {
      set({ isLoadingFetch: false });
    }
  },

  fetchCheckInCheckOutHistory: async (params) => {
    set({ isLoadingHistoryFetch: true });
    try {
      const { history, ...other } = await fetchCheckInCheckOutHistory(params);

      set({
        checkinsHistory: history,
        isLoadingHistoryFetch: false,
        paginationHistory: other,
      });
    } catch (e) {
      console.error(e);
    } finally {
      set({ isLoadingHistoryFetch: false });
    }
  },
  handleUpdateCheckInCheckOut: async (data) => {
    const updated = [data, ...(get().checkins || [])];

    set({ checkins: updated });
  },
}));
